import { useIsRetractionOnly } from "@/hooks/use-is-retraction-only";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import { useShopFlag } from "@/hooks/use-flag.ts";
import { useViewerHasAtLeastCustomerSupportRights } from "@/lib/rights/use-viewer-has-right.ts";
import { useViewerIsSuperAdmin } from "@/lib/rights/use-viewer-is-baback-team.ts";
import useViewerRole from "@/lib/rights/use-viewer-role.ts";
import useTranslation from "@babackapp/translations/use-translation";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@babackapp/ui/components/collapsible";
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@babackapp/ui/components/sidebar";
import { GiftIcon } from "@heroicons/react/24/solid";
import { HomeIcon } from "@radix-ui/react-icons";
import {
  AlignEndHorizontal,
  BarChart3,
  ChevronRight,
  GridIcon,
  ReplyIcon,
  Ruler,
  Telescope,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { NavigationSectionItem } from "./section.tsx";
import { RetractionPortalButton } from "./retraction-portal-button.tsx";

export default function PrimaryNavigation() {
  const { data: shop } = useCurrentShopSuspense();
  const isReturnWidgetV2Enabled = useShopFlag("return-widget-v2");
  const t = useTranslation();
  const isOperationalDashboardEnabled = useShopFlag(
    "isOperationalDashboardEnabled",
  );
  const isSizingEnabled = !!shop?.plan.sizeRecommendation;
  const hasAtLeastCustomerSupportRight =
    useViewerHasAtLeastCustomerSupportRights();
  const isSuperAdmin = useViewerIsSuperAdmin();
  const viewerRole = useViewerRole();
  const isLogistician = viewerRole === "logistician";

  if (!shop) return null;

  const isRetractionOnly = useIsRetractionOnly();

  const navigation = [
    {
      name: t("sidebar.home"),
      href: `/shops/${shop.id}`,
      icon: <HomeIcon className="w-4 h-4" />,
      display:
        !isRetractionOnly &&
        (!isLogistician || shop.preferences.showHomeToLogistician !== false),
    },
    {
      name: t("sidebar.returns"),
      href: `/shops/${shop.id}/returns`,
      icon: <ReplyIcon className="w-4 h-4" />,
      display: true,
    },
    {
      name: t("sidebar.analytics"),
      href: `/shops/${shop.id}/analytics`,
      icon: <BarChart3 className="w-4 h-4" />,
      display:
        !isRetractionOnly &&
        (!isLogistician ||
          shop.preferences.showReturnRateToLogistician !== false),
    },
  ].filter((navItem) => navItem.display);

  const lowerNavigation = [
    {
      name: "Reco produit",
      href: `/shops/${shop.id}/analytics/product-recommendations`,
      icon: <Telescope className="w-4 h-4" />,
      display: isSuperAdmin,
    },
    {
      name: t("sidebar.operational"),
      href: `/shops/${shop.id}/operations`,
      icon: <AlignEndHorizontal className="w-4 h-4" />,
      display: !isRetractionOnly && isOperationalDashboardEnabled,
    },
    {
      name: t("sidebar.giftCode"),
      href: `/shops/${shop.id}/gift-codes`,
      icon: <GiftIcon className="w-4 h-4" />,
      display: !isRetractionOnly && (shop?.giftCode?.activated ?? false),
    },
    {
      name: t("sidebar.automations"),
      href: `/shops/${shop.id}/workflows`,
      icon: <GridIcon className="w-4 h-4" />,
      display: !isRetractionOnly && hasAtLeastCustomerSupportRight,
    },
  ].filter((navItem) => navItem.display);

  return (
    <>
      {navigation.map((navItem) => (
        <NavigationSectionItem
          key={navItem.name}
          to={navItem.href}
          label={navItem.name}
          icon={navItem.icon}
          badge={navItem.badge}
        />
      ))}
      {isSizingEnabled && <SizingNavItem shopId={shop.id} />}
      {lowerNavigation.map((navItem) => (
        <NavigationSectionItem
          key={navItem.name}
          to={navItem.href}
          label={navItem.name}
          icon={navItem.icon}
          badge={navItem.badge}
        />
      ))}
      {isReturnWidgetV2Enabled && <RetractionPortalButton />}
    </>
  );
}

function SizingNavItem({ shopId }: { shopId: string }) {
  const t = useTranslation();
  const { pathname } = useLocation();
  const basePath = `/shops/${shopId}/sizing`;
  const isInSizing = pathname.startsWith(basePath);
  const dashboardPath = `${basePath}/dashboard`;
  const experimentsPath = `${basePath}/experiments`;
  const guidesPath = `${basePath}/guides`;
  const simulatorPath = `${basePath}/simulator`;
  const installPath = `${basePath}/install`;
  const manualBiasPath = `${basePath}/manual-bias`;

  const subItems = [
    { label: t("sidebar.sizingDashboard"), to: dashboardPath },
    { label: "A/B Testing", to: experimentsPath },
    { label: "Guides", to: guidesPath },
    { label: t("sidebar.sizingSimulator"), to: simulatorPath },
    { label: t("sidebar.sizingInstall"), to: installPath },
    { label: t("sidebar.sizingManualBias"), to: manualBiasPath },
  ];

  return (
    <Collapsible open={isInSizing} className="group/sizing">
      <SidebarMenuItem>
        <SidebarMenuButton asChild isActive={isInSizing}>
          <Link to={dashboardPath}>
            <Ruler className="w-4 h-4" />
            <span>{t("sidebar.sizing")}</span>
            <ChevronRight className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/sizing:rotate-90" />
          </Link>
        </SidebarMenuButton>
        <CollapsibleContent>
          <SidebarMenuSub>
            {subItems.map((item) => (
              <SidebarMenuSubItem key={item.to}>
                <SidebarMenuSubButton asChild isActive={pathname === item.to}>
                  <Link to={item.to}>{item.label}</Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

